// Filters available on the galleries, in display order.
//
// There are two kinds, and the components treat them uniformly once configured:
//
//   imageFilters  one value read straight off an image (its year, its artist)
//   tagFilters    a group of tags the image may carry
//
// Adding a filter means adding an entry here plus the data it reads, never
// touching the components.
//
// Within a facet the selection is an OR (one tag at a time), and across filters
// it is an AND.
//
// The options offered by a filter are narrowed by the others (see `facetImages`):
// picking a year leaves only the artists that have images from it.

// Values read off the image itself, so `get` is the only thing that differs
// between them
export const imageFilters = [
  {
    key: "year",
    label: "Year",
    allLabel: "All",
    get: (image) => image.creationDate?.slice(0, 4),
    sort: (a, b) => b - a, // newest first
  },
  {
    key: "artist",
    label: "Artist",
    allLabel: "All",
    get: (image) => image.artist?.name,
    sort: (a, b) => a.localeCompare(b),
  },
];

// Tags in the image data stay plain and independent from this file
// (`tags: ["feral", "dragon"]`), so a facet is just a named group of tags the
// visitor can filter by.
export const tagFilters = [
  {
    key: "body",
    label: "Body type",
    allLabel: "All",
    tags: ["anthro", "feral"],
  },
  {
    key: "species",
    label: "Species",
    allLabel: "All",
    tags: ["avian", "bovid", "canine", "equine", "feline", "fish", "naga", "rodent", "scalie", "taur", "ursine"],
  },
];

// Every key a gallery filter can occupy in the store
export const filterKeys = [
  ...imageFilters.map((filter) => filter.key),
  ...tagFilters.map((facet) => facet.key),
];

// Options for an image filter: only the values this gallery actually has, so the
// visitor can never pick a combination that renders nothing
//
// `selected` is the value currently chosen for this filter. It is always part of
// the options even when the rest of the selection leaves no image carrying it:
// dropping it would show the field as blank with no visible way to change it
export const imageFilterItems = (images, filter, selected) => {
  const values = images.map((image) => filter.get(image)).filter(Boolean);
  if (selected) values.push(selected);
  return [...new Set(values)]
    .sort(filter.sort)
    .map((value) => ({ title: value, value }));
};

// A selection this gallery cannot satisfy is ignored instead of emptying it,
// which keeps the galleries reused by other pages rendering
export const activeImageFilter = (images, filter, selected) =>
  selected && images.some((image) => filter.get(image) === selected)
    ? selected
    : null;

// The images that would still be rendered if `except` were left out of the
// selection. This is what makes the filters narrow each other: the options of a
// filter are read from the set every *other* filter has already narrowed down,
// so a dropdown never offers a value that would render nothing.
//
// A filter is excluded from its own set, so its options are independent of its
// own selection: choosing a year keeps every artist reachable, and the artist
// chosen afterwards stays in the list even if the result ends up empty.
export const facetImages = (images, selected, except) => {
  const byImageValue = imageFilters
    .filter((filter) => filter.key !== except)
    .map((filter) => ({
      filter,
      value: activeImageFilter(images, filter, selected[filter.key]),
    }))
    .filter(({ value }) => value !== null);

  const tags = tagFilters
    .filter((facet) => facet.key !== except)
    .map((facet) => facet.tags.find((tag) => selected[facet.key] === tag))
    .filter((tag) => tag && images.some((image) => image.tags?.includes(tag)));

  return images.filter(
    (image) =>
      byImageValue.every(({ filter, value }) => filter.get(image) === value) &&
      tags.every((tag) => image.tags?.includes(tag))
  );
};
